import { useEffect, useState } from "react";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup, SelectLabel } from "@/components/ui/select";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Building2, Send } from "lucide-react";
import RecipientPicker from "@/components/RecipientPicker";
import LocationPicker from "@/components/LocationPicker";
import ParcelTypeSelect from "@/components/ParcelTypeSelect";
import CourierSelect from "@/components/CourierSelect";

/** Encode a Select value that can represent EITHER a Branch or a Location. */
const encode = (kind, id) => `${kind}:${id}`;
const decode = (v) => {
  if (!v) return { kind: null, id: null };
  const i = v.indexOf(":");
  return { kind: v.slice(0, i), id: v.slice(i + 1) };
};

export default function BranchTransferPage() {
  const { user } = useAuth();
  const isBranchScoped = user && ["branch_admin", "branch_user"].includes(user.role);
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({
    branch_id: "", dest_branch_id: "", dest_branch_contact_email: "",
    // Optional location refinements (frontend-only · saved into sender/recipient address on submit)
    source_location_id: "", source_location_name: "",
    dest_location_id: "", dest_location_name: "",
    mode: "Internal",
    awb_number: "",
    courier: "InternalMail",
    sender_name: "",
    recipient_id: null,
    recipient_name: "", recipient_department: "", recipient_phone: "", recipient_address: "",
    destination_location_id: null,
    shipment_type: "Official",  // Personal | Official — carried into auto-created inward at destination
    route_type: "direct",       // "direct" | "hub"
    hub_branch_id: "",          // required when route_type === "hub"
    weight_kg: 0.5, parcel_type: "Document", ship_product: "Surface", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    // include_all=1 — Branch users must still see all tenant branches for
    // destination + hub selection, even though data queries stay branch-scoped.
    api.get("/branches", { params: { include_all: 1 } }).then(r => {
      setBranches(r.data);
      if (user?.branch_id) setForm(f => ({ ...f, branch_id: user.branch_id }));
    });
    api.get("/locations").then(r => setLocations(r.data || [])).catch(() => setLocations([]));
  }, [user]);

  const set = (k, v) => setForm(s => {
    const n = { ...s, [k]: v };
    if (k === "mode") {
      if (v === "Postal" || v === "Internal") n.courier = "InternalMail";
    }
    return n;
  });

  /** Group locations under each branch when they carry a branch_id; unassigned fall into a shared bucket. */
  const locationsByBranch = (() => {
    const map = new Map();
    const unassigned = [];
    for (const l of locations) {
      if (l.branch_id) {
        if (!map.has(l.branch_id)) map.set(l.branch_id, []);
        map.get(l.branch_id).push(l);
      } else {
        unassigned.push(l);
      }
    }
    return { map, unassigned };
  })();

  const pickBranchOrLocation = (side, encoded) => {
    const { kind, id } = decode(encoded);
    setForm(s => {
      const n = { ...s };
      if (side === "src") {
        if (kind === "b") {
          n.branch_id = id;
          n.source_location_id = ""; n.source_location_name = "";
        } else if (kind === "l") {
          const loc = locations.find(l => l.id === id);
          n.source_location_id = id;
          n.source_location_name = loc?.name || "";
          if (loc?.branch_id) n.branch_id = loc.branch_id;
        }
      } else {
        if (kind === "b") {
          n.dest_branch_id = id;
          n.dest_location_id = ""; n.dest_location_name = "";
        } else if (kind === "l") {
          const loc = locations.find(l => l.id === id);
          n.dest_location_id = id;
          n.dest_location_name = loc?.name || "";
          if (loc?.branch_id) {
            n.dest_branch_id = loc.branch_id;
          } else {
            // Try to auto-resolve nearest branch by pincode → city → any-other-than-source
            const targetPin = String(loc?.pincode || "");
            const targetCity = (loc?.city || "").toLowerCase();
            const candidates = branches.filter(b => b.id !== s.branch_id);
            const near = (targetPin && candidates.find(b => String(b.pincode || "") === targetPin))
                      || (targetCity && candidates.find(b => (b.location || "").toLowerCase().includes(targetCity)))
                      || candidates[0];
            if (near) n.dest_branch_id = near.id;
          }
        }
      }
      return n;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.branch_id) return toast.error("Choose source branch");
    // Destination must be a Branch OR a Location. Branch is auto-derived on the server when only Location is picked.
    if (!form.dest_branch_id && !form.dest_location_id) {
      return toast.error("Choose destination branch or location");
    }
    if (form.dest_branch_id && form.branch_id === form.dest_branch_id) return toast.error("Source and destination must differ");
    if (!form.sender_name || !form.recipient_name) return toast.error("Sender & recipient required");
    if (form.mode === "Courier" && (!form.courier || form.courier === "InternalMail")) return toast.error("Pick a courier for Courier mode");
    if (form.route_type === "hub" && !form.hub_branch_id) return toast.error("Select a Branch Hub or switch to Direct Delivery");
    setBusy(true);
    try {
      const srcLoc = locations.find(l => l.id === form.source_location_id);
      const dstLoc = locations.find(l => l.id === form.dest_location_id);
      const payload = {
        ...form,
        dest_location_id: form.dest_location_id || null,
        route_type: form.route_type || "direct",
        hub_branch_id: form.route_type === "hub" ? form.hub_branch_id : null,
        sender_address: [srcLoc?.name, srcLoc?.address, form.sender_address].filter(Boolean).join(" · "),
        recipient_address: [dstLoc?.name, dstLoc?.address, form.recipient_address].filter(Boolean).join(" · "),
      };
      const { data } = await api.post("/parcels/transfer", payload);
      toast.success(`Transfer created · Reference ${data.tracking_number}`, {
        description: data.awb_number ? `AWB ${data.awb_number}` : undefined,
      });
      nav("/app/parcels");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  // Nearest-branch suggestion for the hub (client-side heuristic — same tenant, prefer pincode, then city).
  const suggestedHubId = (() => {
    const dLoc = locations.find(l => l.id === form.dest_location_id);
    const targetPin = String(dLoc?.pincode || "");
    const targetCity = (dLoc?.city || "").toLowerCase();
    const dstBranchObj = branches.find(b => b.id === form.dest_branch_id);
    const candidates = branches.filter(b => b.id !== form.branch_id);
    return (targetPin && candidates.find(b => String(b.pincode || "") === targetPin)?.id)
        || (targetCity && candidates.find(b => (b.location || "").toLowerCase().includes(targetCity))?.id)
        || (dstBranchObj?.id)
        || candidates[0]?.id
        || "";
  })();

  useEffect(() => {
    if (form.route_type === "hub" && !form.hub_branch_id && suggestedHubId) {
      setForm(s => ({ ...s, hub_branch_id: suggestedHubId }));
    }
    // eslint-disable-next-line
  }, [form.route_type, suggestedHubId]);

  const destinationBranch = branches.find(b => b.id === form.dest_branch_id);
  const encodedSrc = form.source_location_id ? encode("l", form.source_location_id) : (form.branch_id ? encode("b", form.branch_id) : "");
  const encodedDst = form.dest_location_id ? encode("l", form.dest_location_id) : (form.dest_branch_id ? encode("b", form.dest_branch_id) : "");

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-3xl">
      <header>
        <div className="overline flex items-center gap-2"><Building2 size={12}/> Internal movement</div>
        <h1 className="text-4xl font-display mt-1">Branch Transfer.</h1>
        <p className="text-sm text-muted-foreground mt-1">One parcel, two locations — logs as <b>outward</b> at source and <b>inward</b> at destination automatically.</p>
      </header>

      <form onSubmit={submit} className="border border-border rounded-md bg-card p-5 md:p-6 space-y-5 card-elev" data-testid="transfer-form">
        {/* Reference ID (auto) + AWB row — matches Inward/Outward layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="overline flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary" />
              Internal Reference ID
              <span className="ml-1 text-[9px] font-bold text-primary bg-red-50 border border-primary px-1.5 py-0.5 rounded">AUTO</span>
            </span>
            <input readOnly value="MR-{TENANT_CODE}-{YYMMDD}-#### (assigned on save)"
              data-testid="transfer-reference-preview"
              className="mt-1 w-full h-10 px-3 rounded-md border border-dashed border-primary/40 bg-red-50/40 text-xs font-mono text-primary/80 cursor-not-allowed" />
            <div className="text-[10px] text-muted-foreground mt-1">Generated automatically on submit (same series as Inward &amp; Outward).</div>
          </div>
          <div>
            <span className="overline">AWB / Consignment #</span>
            <Input value={form.awb_number} onChange={(e) => set("awb_number", e.target.value)}
              data-testid="transfer-awb"
              placeholder="External courier's tracking number (optional)"
              className="mt-1 rounded-md border-input font-mono focus-visible:ring-primary" />
            <div className="text-[10px] text-muted-foreground mt-1">Optional &mdash; links this transfer to the courier&apos;s tracking portal.</div>
          </div>
        </div>

        {/* Branches OR Locations — one dropdown per side · grouped picker (frontend-only) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-border pt-4">
          <label>
            <span className="overline">Source branch / location *</span>
            {isBranchScoped ? (
              <input
                readOnly
                data-testid="transfer-src-locked"
                value={branches.find(b => b.id === form.branch_id)?.name || "Loading..."}
                className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-muted/40 text-sm cursor-not-allowed"
                title="Locked to your assigned branch"
              />
            ) : (
              <Select value={encodedSrc} onValueChange={(v) => pickBranchOrLocation("src", v)}>
                <SelectTrigger data-testid="transfer-src" className="rounded-md mt-1"><SelectValue placeholder="Choose source" /></SelectTrigger>
                <SelectContent>
                  {branches.map(b => {
                    const inBranch = locationsByBranch.map.get(b.id) || [];
                    return (
                      <SelectGroup key={b.id}>
                        <SelectItem value={encode("b", b.id)}>{b.name}</SelectItem>
                        {inBranch.map(l => (
                          <SelectItem key={l.id} value={encode("l", l.id)} className="pl-8 text-xs">
                            &nbsp;&nbsp;• {l.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    );
                  })}
                  {locationsByBranch.unassigned.length > 0 && (
                    <SelectGroup>
                      <SelectLabel className="text-[10px] uppercase tracking-widest text-muted-foreground pt-2">Locations</SelectLabel>
                      {locationsByBranch.unassigned.map(l => (
                        <SelectItem key={l.id} value={encode("l", l.id)} className="pl-6 text-xs">
                          • {l.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
            )}
          </label>
          <label>
            <span className="overline">Destination branch / location *</span>
            <Select value={encodedDst} onValueChange={(v) => pickBranchOrLocation("dst", v)}>
              <SelectTrigger data-testid="transfer-dest" className="rounded-md mt-1"><SelectValue placeholder="Choose destination" /></SelectTrigger>
              <SelectContent>
                {branches.filter(b => b.id !== form.branch_id).map(b => {
                  const inBranch = locationsByBranch.map.get(b.id) || [];
                  return (
                    <SelectGroup key={b.id}>
                      <SelectItem value={encode("b", b.id)}>{b.name}</SelectItem>
                      {inBranch.map(l => (
                        <SelectItem key={l.id} value={encode("l", l.id)} className="pl-8 text-xs">
                          &nbsp;&nbsp;• {l.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  );
                })}
                {locationsByBranch.unassigned.length > 0 && (
                  <SelectGroup>
                    <SelectLabel className="text-[10px] uppercase tracking-widest text-muted-foreground pt-2">Locations</SelectLabel>
                    {locationsByBranch.unassigned.map(l => (
                      <SelectItem key={l.id} value={encode("l", l.id)} className="pl-6 text-xs">
                        • {l.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
          </label>
        </div>

        {/* Route preview — shows Branch - Location on each side */}
        {form.branch_id && form.dest_branch_id && (
          <div className="text-sm bg-primary/5 border border-primary/20 rounded-md p-3" data-testid="transfer-route-preview">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-semibold">{branches.find(b => b.id === form.branch_id)?.name}</div>
              {form.source_location_name && (
                <>
                  <span className="text-muted-foreground">-</span>
                  <div className="font-semibold text-primary/80">{form.source_location_name}</div>
                </>
              )}
              <ArrowRight size={14} className="text-primary mx-1" />
              <div className="font-semibold">{destinationBranch?.name}</div>
              {form.dest_location_name && (
                <>
                  <span className="text-muted-foreground">-</span>
                  <div className="font-semibold text-primary">{form.dest_location_name}</div>
                </>
              )}
              {!form.dest_location_name && destinationBranch?.location && (
                <div className="text-xs text-muted-foreground">· {destinationBranch.location}</div>
              )}
            </div>
          </div>
        )}

        {/* Mode + optional courier */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label>
            <span className="overline">Mode *</span>
            <Select value={form.mode} onValueChange={(v) => set("mode", v)}>
              <SelectTrigger data-testid="transfer-mode" className="rounded-md mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Courier">Courier</SelectItem>
                <SelectItem value="Postal">Postal (India Post)</SelectItem>
                <SelectItem value="Internal">Internal (in-house runner)</SelectItem>
              </SelectContent>
            </Select>
          </label>
          {form.mode === "Courier" && (
            <div data-testid="transfer-courier-wrap">
              <span className="overline">Courier *</span>
              <div className="mt-1"><CourierSelect value={form.courier} onChange={(v) => set("courier", v)} testid="transfer-courier"/></div>
            </div>
          )}
        </div>

        {/* Contact */}
        <label>
          <span className="overline">Destination contact email <span className="text-muted-foreground normal-case tracking-normal">(optional — receives arrival notice)</span></span>
          <Input data-testid="transfer-contact-email" type="email" value={form.dest_branch_contact_email}
            onChange={(e) => set("dest_branch_contact_email", e.target.value)}
            className="mt-1 rounded-md" placeholder="branch.admin@company.com" />
        </label>

        {/* People — Sender from RecipientPicker (persons master), Destination from LocationPicker (locations master) */}
        <div className="border-t border-border pt-4 space-y-3">
          <div className="overline">Parcel contents</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <RecipientPicker
                value={form.sender_name ? { id: null, name: form.sender_name, department: "" } : null}
                onChange={(r) => setForm(s => ({ ...s, sender_name: r.name }))}
                testid="transfer-sender-picker"
                label="Sender (at source · employee)"
              />
              <Input data-testid="transfer-sender" required value={form.sender_name}
                onChange={(e)=>set("sender_name",e.target.value)}
                placeholder="Sender name (override)" className="mt-2 rounded-md" />
            </div>
            <div>
              <RecipientPicker
                value={form.recipient_id ? { id: form.recipient_id, name: form.recipient_name, department: form.recipient_department } : null}
                onChange={(r) => setForm(s => ({ ...s, recipient_id: r.id, recipient_name: r.name, recipient_department: r.department || "", recipient_phone: r.phone || "" }))}
                testid="transfer-recipient-picker"
                label="Recipient at destination (employee)"
              />
              <Input data-testid="transfer-recipient-name" placeholder="Recipient name (override)" required value={form.recipient_name}
                onChange={(e)=>set("recipient_name", e.target.value)} className="mt-2 rounded-md" />
            </div>
          </div>
          <LocationPicker
            value={form.destination_location_id ? { id: form.destination_location_id, name: form.recipient_name, address: form.recipient_address } : null}
            onChange={(l) => setForm(s => ({
              ...s,
              destination_location_id: l.id,
              recipient_name: l.type === "person" ? l.name : (l.contact_person || l.name),
              recipient_address: l.address || s.recipient_address,
              recipient_phone: l.phone || s.recipient_phone,
            }))}
            testid="transfer-location-picker"
            label="Or drop point (Locations master)"
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <label>
            <span className="overline">Weight (kg)</span>
            <Input data-testid="transfer-weight" type="number" step="0.1" value={form.weight_kg}
              onChange={(e)=>set("weight_kg", parseFloat(e.target.value)||0)} className="mt-1 rounded-md font-mono" />
          </label>
          <label>
            <span className="overline">Ship Product</span>
            <select data-testid="transfer-ship-product" value={form.ship_product || "Surface"}
              onChange={(e)=>set("ship_product", e.target.value)}
              className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none">
              <option value="Air Doc">Air Doc</option>
              <option value="Air Non-Doc">Air Non-Doc</option>
              <option value="Surface">Surface</option>
              <option value="Cargo">Cargo</option>
            </select>
          </label>
          <div>
            <span className="overline">Type</span>
            <ParcelTypeSelect value={form.parcel_type} onChange={(v)=>set("parcel_type",v)} testid="transfer-type" />
          </div>
        </div>
        <label className="block">
          <span className="overline">Remarks</span>
          <Input data-testid="transfer-notes" value={form.notes} onChange={(e)=>set("notes",e.target.value)} className="mt-1 rounded-md" />
        </label>

        {/* Shipment classification — carried into the auto-created destination Inward Entry */}
        <div className="border-t border-border pt-3">
          <div className="overline mb-2">Classify parcel *</div>
          <div className="inline-flex border border-border rounded-md overflow-hidden" data-testid="transfer-shipment-type">
            {["Official", "Personal"].map((t) => (
              <button
                key={t} type="button"
                data-testid={`transfer-ship-${t.toLowerCase()}`}
                onClick={() => set("shipment_type", t)}
                className={`px-4 py-1.5 text-sm uppercase tracking-widest ${form.shipment_type === t ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1.5">
            This classification is carried into the destination branch's auto-generated Inward Entry.
          </div>
        </div>

        {/* Route Type — Direct Delivery (default) or Via Branch Hub with AI-suggested nearest hub */}
        <div className="border-t border-border pt-3" data-testid="transfer-route-block">
          <div className="overline mb-2">Route type *</div>
          <div className="flex flex-wrap items-start gap-4">
            {[
              { v: "direct", label: "Direct Delivery" },
              { v: "hub", label: "Via Branch Hub" },
            ].map((opt) => (
              <label key={opt.v} className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid={`transfer-route-${opt.v}`}>
                <input type="radio" name="route_type" value={opt.v}
                       checked={form.route_type === opt.v}
                       onChange={() => set("route_type", opt.v)}
                       className="accent-[#BE1E1E]" />
                <span className={form.route_type === opt.v ? "font-semibold" : ""}>{opt.label}</span>
              </label>
            ))}
          </div>

          {form.route_type === "hub" && (
            <div className="mt-3 rounded-md border border-primary/30 bg-primary/5 p-3" data-testid="transfer-hub-panel">
              <div className="text-[11px] text-muted-foreground mb-1.5">
                <b>AI-suggested hub:</b>{" "}
                {branches.find(b => b.id === suggestedHubId)?.name || "—"}
                {" · you can accept or choose another hub below."}
              </div>
              <label className="block">
                <span className="overline">Branch Hub *</span>
                <Select value={form.hub_branch_id || ""} onValueChange={(v) => set("hub_branch_id", v)}>
                  <SelectTrigger data-testid="transfer-hub" className="rounded-md mt-1">
                    <SelectValue placeholder="Choose Branch Hub" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches
                      .filter(b => b.id !== form.branch_id)
                      .map(b => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.name}{b.id === suggestedHubId ? " · Suggested" : ""}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </label>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-2 border-t border-border">
          <Button type="submit" data-testid="transfer-submit" disabled={busy}
            className="rounded-md bg-primary text-primary-foreground shadow-brut-orange h-11 px-5">
            <Send size={14} className="mr-2" /> {busy ? "Creating…" : "Create branch transfer"}
          </Button>
        </div>
      </form>
    </div>
  );
}
